import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from './services/supabase';
import { checkSession } from './store/slices/authSlice';
import { RootState, AppDispatch } from './store';
import googleAnalytics from './services/googleAnalyticsService';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './components/dashboard/DashboardPage';
import RankingsPage from './pages/rankings/RankingsPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import KeywordResearchPage from './pages/keyword-research/KeywordResearchPage';
import UrlManagementPage from './pages/url-management/UrlManagementPage';
import CompetitorsPage from './pages/competitors/CompetitorsPage';
import ReportsPage from './pages/reports/ReportsPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import NotFoundPage from './pages/errors/NotFoundPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { RankingOnDemandPage } from './pages/ranking-on-demand/RankingOnDemandPage';
import { GenerativeSearchPage } from './pages/generative-search/GenerativeSearchPage';
import MetaDescriptionPage from './pages/meta-description/MetaDescriptionPage';
import AIRoastPage from './pages/ai-roast/AIRoastPage';
import { PAATreesPage } from './pages/paa-trees/PAATreesPage';
import { PAATreeViewer } from './pages/paa-trees/PAATreeViewer';
import SchemaGeneratorPage from './pages/schema/SchemaGeneratorPage';
import { ContentProjectsPage } from './pages/content-projects/ContentProjectsPage';
import { ProjectResearchHub } from './pages/content-projects/ProjectResearchHub';
import DynamicContentPage from './pages/dynamic-content/DynamicContentPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Google Analytics Route Tracker
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    googleAnalytics.trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { isDarkMode } = useSelector((state: RootState) => state.ui);
  
  // Create theme based on dark mode preference
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#4e6bff', // Primary blue
      },
      secondary: {
        main: '#f7b731', // Secondary yellow
      },
      error: {
        main: '#d61f26', // Error red
      },
      success: {
        main: '#28a745', // Success green
      },
    },
    typography: {
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
      },
      h2: {
        fontWeight: 700,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 500,
      },
      h6: {
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });
  
  useEffect(() => {
    // Initialize Google Analytics
    googleAnalytics.initialize();
    
    // Check for existing Supabase session
    dispatch(checkSession());
  }, [dispatch]);
  
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3}>
          <CssBaseline />
          <BrowserRouter>
            <RouteTracker />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/rankings" element={<RankingsPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/keyword-research" element={<KeywordResearchPage />} />
                <Route path="/ranking-on-demand" element={<RankingOnDemandPage />} />
                <Route path="/generative-search" element={<GenerativeSearchPage />} />
                <Route path="/paa-trees" element={<PAATreesPage />} />
                <Route path="/paa-trees/:treeId" element={<PAATreeViewer />} />
                <Route path="/meta-descriptions" element={<MetaDescriptionPage />} />
                <Route path="/ai-roast" element={<AIRoastPage />} />
                <Route path="/schema-generator" element={<SchemaGeneratorPage />} />
                <Route path="/content-projects" element={<ContentProjectsPage />} />
                <Route path="/content-projects/:projectId" element={<ProjectResearchHub />} />
                <Route path="/dynamic-content" element={<DynamicContentPage />} />
                <Route path="/url-management" element={<UrlManagementPage />} />
                <Route path="/competitors" element={<CompetitorsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              
              {/* 404 page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;